// Comments on the canvas: pins that follow the element they sit on, the box a
// new comment is typed in, a pin's popover, and the side panel.
//
// It is rendered inside the canvas holder, as a sibling of Excalidraw. It owns
// the picture of the scene (scroll, zoom, elements) so that a drag redraws the
// pins without re-rendering the board page around it.

import { sceneCoordsToViewportCoords, viewportCoordsToSceneCoords } from "@excalidraw/excalidraw";
import { describeAnchor, hitTest, liveById, pinPoint, positionIn, threadsOf } from "@hivemind-canvas/shared/comments";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { initialsOf } from "../ui/format.js";
import { CommentsPanel, PANEL_WIDTH } from "./CommentsPanel.jsx";
import { ReplyBox, Thread } from "./Thread.jsx";

const POPOVER_WIDTH = 320;
const MARGIN = 8;
const PIN_SIDE = 34; // from the clicked spot to just past the pin's right edge
const PIN_TOP = 30; // from the clicked spot up to the pin's top edge
const HIT_SLOP_PX = 6;

const KIND = { frame: "a section", rectangle: "a box", ellipse: "an ellipse", diamond: "a diamond", arrow: "an arrow", line: "a line", text: "some text" };

const oneLine = (text, max = 48) => {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
};

/** Scroll, zoom and elements as of the last change, once per frame. */
const useSceneView = (api) => {
  const [view, setView] = useState(null);
  useEffect(() => {
    if (!api) return undefined;
    let frame = 0;
    const refresh = () => {
      frame = 0;
      const app = api.getAppState();
      setView({
        elements: api.getSceneElements(),
        zoom: app.zoom.value,
        scrollX: app.scrollX,
        scrollY: app.scrollY,
        width: app.width,
        height: app.height,
      });
    };
    const stop = api.onChange(() => {
      if (!frame) frame = requestAnimationFrame(refresh);
    });
    refresh();
    return () => {
      stop();
      cancelAnimationFrame(frame);
    };
  }, [api]);
  return view;
};

const Pin = ({ name, x, y, active, gone, agent, draft, onClick }) => (
  <button
    type="button"
    className={`comment-pin comment-ui${active ? " active" : ""}${gone ? " gone" : ""}${agent ? " agent" : ""}${draft ? " draft" : ""}`}
    style={{ left: x, top: y }}
    aria-label={gone ? `Comment by ${name} (the element was deleted)` : `Comment by ${name}`}
    onClick={onClick}
  >
    {initialsOf(name).slice(0, 1)}
  </button>
);

/** A card next to a point, kept inside the canvas whatever its height turns out to be. */
const Popover = ({ at, bounds, children }) => {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: POPOVER_WIDTH, h: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setSize((now) => (now.w === el.offsetWidth && now.h === el.offsetHeight ? now : { w: el.offsetWidth, h: el.offsetHeight }));
  });

  let left = at.x + PIN_SIDE;
  if (left + size.w > bounds.w - MARGIN) left = at.x - 12 - size.w;
  left = Math.max(MARGIN, left);
  const top = Math.max(MARGIN, Math.min(at.y - PIN_TOP, bounds.h - size.h - MARGIN));

  return (
    <div ref={ref} className="comment-pop comment-ui" style={{ left, top, width: POPOVER_WIDTH }}>
      {children}
    </div>
  );
};

/**
 * @param {{excalidrawAPI: object|null, comments: object[], me: {name: string}|null,
 *   mode: boolean, onMode: (on: boolean) => void, panelOpen: boolean, onPanel: (open: boolean) => void,
 *   activeId: string|null, onActive: (id: string|null) => void,
 *   onAdd: (payload: object) => Promise, onResolve: (id: string, resolved: boolean) => Promise}} props
 */
export const Comments = ({ excalidrawAPI, comments, me, mode, onMode, panelOpen, onPanel, activeId, onActive, onAdd, onResolve }) => {
  const layerRef = useRef(null);
  const view = useSceneView(excalidrawAPI);
  const [composer, setComposer] = useState(null); // the anchor a new comment is being typed for

  const threads = useMemo(() => threadsOf(comments), [comments]);
  const live = useMemo(() => liveById(view?.elements ?? []), [view]);

  const toScreen = useCallback(
    (point) =>
      sceneCoordsToViewportCoords(
        { sceneX: point.x, sceneY: point.y },
        { zoom: { value: view.zoom }, scrollX: view.scrollX, scrollY: view.scrollY, offsetLeft: 0, offsetTop: 0 },
      ),
    [view],
  );

  const whereOf = useCallback(
    (root) => {
      const { on, gone } = describeAnchor(live, root.anchor);
      if (gone) return { text: "Element deleted", gone: true };
      const el = root.anchor.elementId ? live.get(root.anchor.elementId) : null;
      if (!el) return { text: "On the canvas", gone: false };
      return { text: on ? `On "${oneLine(on)}"` : `On ${KIND[el.type] ?? "an element"}`, gone: false };
    },
    [live],
  );

  const pins = useMemo(() => {
    if (!view) return [];
    return threads
      .filter(({ root }) => !root.resolved)
      .map((thread) => {
        const point = pinPoint(live, thread.root.anchor);
        return { thread, gone: point.gone, ...toScreen(point) };
      })
      .filter((p) => p.x > -40 && p.y > -40 && p.x < view.width + 40 && p.y < view.height + 40);
  }, [threads, live, view, toScreen]);

  // ---- a click while in comment mode opens the box ----

  const place = useCallback(
    (clientX, clientY) => {
      const app = excalidrawAPI.getAppState();
      const point = viewportCoordsToSceneCoords({ clientX, clientY }, app);
      const hit = hitTest(excalidrawAPI.getSceneElements(), point, HIT_SLOP_PX / app.zoom.value);
      setComposer(hit ? { elementId: hit.id, ...positionIn(hit, point.x, point.y), ...point } : point);
    },
    [excalidrawAPI],
  );

  useEffect(() => {
    if (!mode) {
      setComposer(null);
      return undefined;
    }
    const holder = layerRef.current?.parentElement;
    if (!holder) return undefined;

    // Only the drawing surface is taken over, so Excalidraw's own toolbar still works.
    const onCanvas = (event) => event.target instanceof HTMLCanvasElement;
    const swallow = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    const down = (event) => {
      if (event.target.closest?.(".comment-ui")) return;
      if (onCanvas(event)) swallow(event);
      else onMode(false); // reaching for another tool ends comment mode
    };
    const up = (event) => {
      if (!onCanvas(event)) return;
      swallow(event);
      if (event.button === 0) place(event.clientX, event.clientY);
    };
    const quiet = (event) => {
      if (onCanvas(event)) swallow(event);
    };
    const others = ["mousedown", "mouseup", "click", "dblclick"];
    holder.addEventListener("pointerdown", down, true);
    holder.addEventListener("pointerup", up, true);
    for (const type of others) holder.addEventListener(type, quiet, true);
    return () => {
      holder.removeEventListener("pointerdown", down, true);
      holder.removeEventListener("pointerup", up, true);
      for (const type of others) holder.removeEventListener(type, quiet, true);
    };
  }, [mode, onMode, place]);

  // A pin sits on top of the canvas, so a touchpad scroll or pinch with the pointer
  // on it would stop at the pin and the board would freeze. Hand it to the canvas below.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return undefined;
    const pass = (event) => {
      const below = document.elementsFromPoint(event.clientX, event.clientY).find((e) => e instanceof HTMLCanvasElement);
      if (!below) return;
      event.preventDefault();
      below.dispatchEvent(new WheelEvent("wheel", event));
    };
    layer.addEventListener("wheel", pass, { passive: false });
    return () => layer.removeEventListener("wheel", pass);
  }, []);

  // A click anywhere else on the board closes the open thread.
  useEffect(() => {
    const holder = layerRef.current?.parentElement;
    if (!activeId || !holder) return undefined;
    const down = (event) => {
      if (!event.target.closest?.(".comment-ui")) onActive(null);
    };
    holder.addEventListener("pointerdown", down, true);
    return () => holder.removeEventListener("pointerdown", down, true);
  }, [activeId, onActive]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        if (mode || composer) {
          setComposer(null);
          onMode(false);
        } else if (activeId) onActive(null);
        return;
      }
      const typing = event.target.closest?.("input, textarea, select, [contenteditable='true']");
      if (event.key.toLowerCase() === "c" && !typing && !event.metaKey && !event.ctrlKey && !event.altKey && !event.repeat) {
        event.preventDefault();
        onMode(!mode);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, composer, activeId, onMode, onActive]);

  // ---- what a thread does ----

  const reply = useCallback((parentId, body) => onAdd({ body, parentId }), [onAdd]);

  const resolve = useCallback(
    async (id, resolved) => {
      await onResolve(id, resolved);
      if (resolved && id === activeId) onActive(null);
    },
    [onResolve, activeId, onActive],
  );

  const submitNew = async (body) => {
    await onAdd({ body, anchor: composer });
    setComposer(null);
    onMode(false);
  };

  /** Bring a thread's pin into view, clear of the panel, and open it. */
  const focus = useCallback(
    ({ root }) => {
      onActive(root.resolved ? null : root.id);
      if (root.resolved) return;
      const app = excalidrawAPI.getAppState();
      const point = pinPoint(live, root.anchor);
      const freeWidth = app.width - (panelOpen ? PANEL_WIDTH : 0);
      excalidrawAPI.updateScene({
        appState: {
          scrollX: freeWidth / 2 / app.zoom.value - point.x,
          scrollY: app.height / 2 / app.zoom.value - point.y,
        },
      });
    },
    [excalidrawAPI, live, panelOpen, onActive],
  );

  const bounds = view ? { w: view.width - (panelOpen ? PANEL_WIDTH : 0), h: view.height } : null;
  const activePin = pins.find((p) => p.thread.root.id === activeId);
  const draft = composer && view ? { ...toScreen(pinPoint(live, composer)) } : null;

  return (
    <>
      <div className="comment-pins" ref={layerRef}>
        {pins.map((pin) => (
          <Pin
            key={pin.thread.root.id}
            name={pin.thread.root.author.name}
            x={pin.x}
            y={pin.y}
            gone={pin.gone}
            agent={pin.thread.root.author.kind === "agent"}
            active={pin.thread.root.id === activeId}
            onClick={() => onActive(pin.thread.root.id === activeId ? null : pin.thread.root.id)}
          />
        ))}
        {draft ? <Pin draft name={me?.name ?? "You"} x={draft.x} y={draft.y} /> : null}
      </div>

      <div className="comment-pops">
        {draft ? (
          <Popover at={draft} bounds={bounds}>
            <ReplyBox
              autoFocus
              placeholder="Add a comment"
              submitLabel="Comment"
              hint="Enter sends, Shift+Enter starts a new line"
              onSubmit={submitNew}
              onCancel={() => {
                setComposer(null);
                onMode(false);
              }}
            />
          </Popover>
        ) : null}
        {activePin ? (
          <Popover at={activePin} bounds={bounds}>
            {/* Keyed by thread, so a reply typed in one thread never carries over to the next pin opened. */}
            <Thread
              key={activePin.thread.root.id}
              thread={activePin.thread}
              where={whereOf(activePin.thread.root).text}
              gone={activePin.gone}
              onReply={reply}
              onResolve={resolve}
            />
          </Popover>
        ) : null}
      </div>

      {panelOpen ? (
        <CommentsPanel
          threads={threads}
          whereOf={whereOf}
          activeId={activeId}
          onFocus={focus}
          onReply={reply}
          onResolve={resolve}
          onClose={() => onPanel(false)}
        />
      ) : null}
    </>
  );
};
